import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Copy } from "lucide-react";
import { useStore, api } from "@/context/StoreContext";
import PaymentFollowUp from "./PaymentFollowUp";

const EMPTY = {
  name: "", phone: "", email: "", province: "", district: "",
  commune: "", address: "", delivery: "", remark: "", telegram_phone: "",
};

const QR_NOTE_KH = "សូមស្កេន QR Code ដើម្បីទូទាត់ ហើយសូមបញ្ចូល Order Code របស់អ្នកក្នុង Remark/Note។ សូមអរគុណ🙏🏻";
const QR_NOTE_EN = "Please scan the QR code to complete your payment. And please enter your PaMak Order Code in Remark to help us identify your payment. Thanks🙏🏻";

const TG_PHONE_RE = /^\+?[0-9][0-9\s\-()]{6,19}$/;

export default function CheckoutModal() {
  const {
    checkoutOpen, setCheckoutOpen, cart, cartTotal, money, t, displayName,
    clearCart, toast, config, user, openAuth, lineTotal,
  } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [payment, setPayment] = useState("ABA PayWay");
  const [error, setError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payData, setPayData] = useState(null);
  const [payError, setPayError] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoMsg, setPromoMsg] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [linkOpened, setLinkOpened] = useState(false);

  useEffect(() => {
    if (checkoutOpen && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        phone: f.phone || user.phone || "",
        email: f.email || user.email || "",
      }));
    }
  }, [checkoutOpen, user]);

  if (!checkoutOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => {
    setCheckoutOpen(false);
    setConfirmed(null);
    setCopied(false);
    setPayData(null);
    setPayError(null);
    setPromoInput("");
    setPromoInfo(null);
    setPromoMsg(null);
  };

  const cartItemsPayload = () =>
    cart.map((i) => ({
      product_id: i.productId,
      qty: i.qty,
      options: i.options || null,
      addons: (i.addons || []).map((a) => ({ id: a.id, qty: a.qty })),
    }));

  const applyPromo = async (e) => {
    e.preventDefault();
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    setPromoMsg(null);
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput.trim(), email: form.email, items: cartItemsPayload() });
      if (data.valid) {
        setPromoInfo(data);
        setPromoMsg({ ok: true, text: `${t("checkout.promoApplied")} ${t("checkout.youSaved")} ${money(data.discount)}.` });
        toast(`${t("checkout.promoApplied")} ${t("checkout.youSaved")} ${money(data.discount)}.`);
      } else {
        setPromoInfo(null);
        setPromoMsg({ ok: false, text: data.message || t("checkout.promoInvalid") });
      }
    } catch (err) {
      setPromoInfo(null);
      setPromoMsg({ ok: false, text: err.response?.data?.detail || t("checkout.promoInvalid") });
    }
    setPromoBusy(false);
  };

  const removePromo = () => {
    setPromoInput("");
    setPromoInfo(null);
    setPromoMsg(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "province", "district"];
    if (required.some((f) => !form[f].trim())) {
      setError(t("checkout.error"));
      return;
    }
    if (payment === "Other" && !TG_PHONE_RE.test(form.telegram_phone.trim())) {
      setError(t("checkout.telegramPhoneError"));
      return;
    }
    setError("");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        payment,
        telegram_phone: payment === "Other" ? form.telegram_phone.trim() : "",
        promo_code: promoInfo?.code || "",
        items: cartItemsPayload(),
      });
      setConfirmed(data);
      setCopied(false);
      setLinkOpened(false);
      clearCart();
      setPromoInput("");
      setPromoInfo(null);
      setPromoMsg(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast(typeof d === "string" ? d : t("toast.orderFail"));
      if (typeof d === "string") setError(d);
    }
    setPlacing(false);
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(confirmed.order_number);
    setCopied(true);
    toast(t("toast.copied"));
  };

  const continuePayway = async () => {
    setPayLoading(true);
    setPayError(null);
    try {
      const { data } = await api.post("/payments/create", { order_number: confirmed.order_number });
      if (data.mode === "merchant" && data.qr_image) {
        // v10 future path: automated PayWay verification re-enabled server-side.
        setPayData(data);
        setPayError(null);
        toast(t("confirm.qrReady"));
      } else if (data.payway_url) {
        // v10 manual mode: the owner's official PayWay payment link — new tab, order page stays open.
        window.open(data.payway_url, "_blank", "noopener");
        setLinkOpened(true);
      } else {
        toast(t("toast.tryAgain"));
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object") {
        setPayError(d);
        toast(d.message || t("toast.tryAgain"));
      } else {
        toast(typeof d === "string" ? d : t("toast.tryAgain"));
      }
    }
    setPayLoading(false);
  };

  const method = confirmed?.payment?.method || "";
  const payStatus = confirmed?.payment_status || "pending";
  const automation = !!config.payway_automation;
  const statusLabel = payStatus === "confirmed" ? t("payment.confirmed") : payStatus === "submitted" ? t("payment.submitted") : payStatus === "contact_required" ? t("payment.contact_required") : t("payment.pending");
  const statusClass = payStatus === "confirmed" ? "confirmed" : payStatus === "submitted" ? "submitted" : payStatus === "contact_required" ? "contact" : "pending";

  return (
    <div className={`modal ${checkoutOpen ? "show" : ""}`} data-testid="checkout-modal">
      <div className="modal-card" data-lenis-prevent>
        {confirmed ? (
          <div className="confirm-view" data-testid="order-confirmation">
            <div className="confirm-head">
              <span className="confirm-check"><Check size={26} /></span>
              <div>
                <p className="section-title-small">PAMAK</p>
                <h2>{t("confirm.title")}</h2>
              </div>
              <button className="close-btn" data-testid="confirmation-close-button" aria-label="Close" onClick={close}>
                <X size={18} />
              </button>
            </div>

            <div className={`confirm-status ${statusClass}`} data-testid="payment-status">
              {statusLabel}
            </div>

            <div className="confirm-step" data-testid="confirm-step-1">
              <span className="step-tag">STEP 1</span>
              <div className="pay-meta">
                <div className="pay-meta-row">
                  <span>{t("confirm.orderCodeLabel")}</span>
                  <strong data-testid="order-id">{confirmed.order_number}</strong>
                </div>
                <div className="pay-meta-row">
                  <span>{t("confirm.amountLabel")}</span>
                  <strong data-testid="confirm-amount">{money(confirmed.total)}</strong>
                </div>
              </div>
              <button className="confirm-copy" data-testid="copy-order-code" onClick={copyCode}>
                <Copy size={13} /> {copied ? t("toast.copied") : t("confirm.copy")}
              </button>
            </div>

            {method === "ABA PayWay" && payStatus === "pending" && !(automation && payData) && (
              <div className="confirm-step" data-testid="payway-step">
                <span className="step-tag">STEP 2</span>
                <p className="muted" data-testid="payway-redirect-note">{t("confirm.paywayRedirect")}</p>
                <button
                  className="checkout-btn pay-btn"
                  data-testid="payway-pay-button"
                  onClick={continuePayway}
                  disabled={payLoading}
                >
                  {payLoading ? "…" : t("confirm.payWithPayway")}
                </button>
                {linkOpened && (
                  <p className="small-note" data-testid="payway-link-opened" style={{ color: "#1e7d4f", fontWeight: 700 }}>
                    ✓ {t("confirm.paywayOpened")}
                  </p>
                )}
                {automation && payError && (
                  <div className="payway-diag error" data-testid="payway-error-diagnostics">
                    <p className="diag-title">{t("confirm.diagErrorTitle")}</p>
                    <div className="diag-row"><span>{t("confirm.diagEnv")}</span><strong data-testid="payway-diag-error-env">{payError.environment || "SANDBOX"}</strong></div>
                    <div className="diag-row"><span>{t("confirm.diagTranId")}</span><strong data-testid="payway-diag-error-tran-id">{payError.tran_id || "—"}</strong></div>
                    <div className="diag-row"><span>HTTP</span><strong data-testid="payway-diag-error-http">{payError.payway_http_status ?? "—"}</strong></div>
                    <div className="diag-row"><span>{t("confirm.diagStatus")}</span><strong data-testid="payway-diag-error-status-code">{payError.payway_status_code || "—"}</strong></div>
                    <div className="diag-row"><span>{t("confirm.diagMessage")}</span><strong data-testid="payway-diag-error-status-message">{payError.payway_status_message || "—"}</strong></div>
                    {payError.payway_trace_id && <div className="diag-row"><span>{t("confirm.diagTrace")}</span><strong data-testid="payway-diag-error-trace-id">{payError.payway_trace_id}</strong></div>}
                  </div>
                )}
              </div>
            )}

            {method === "ABA PayWay" && payStatus === "pending" && automation && payData && (
              <div className="confirm-step qr-step" data-testid="payway-live-step">
                <span className="step-tag">STEP 2</span>
                <p className="muted">{t("confirm.paywayScanHint")}</p>
                {(payData.environment || "SANDBOX") === "SANDBOX" && (
                  <p className="payway-sandbox-note" data-testid="payway-sandbox-notice">{t("confirm.sandboxNotice")}</p>
                )}
                {payData.qr_image && <img src={payData.qr_image} className="qr-img" data-testid="payway-qr" alt="PayWay KHQR" />}
                <p className="remark-line">{t("confirm.amountLabel")} <strong data-testid="payway-live-amount">{money(payData.amount)}</strong></p>
                {payData.abapay_deeplink && (
                  <a className="checkout-btn pay-btn" href={payData.abapay_deeplink} data-testid="abapay-deeplink-button">
                    {t("confirm.openAbaApp")}
                  </a>
                )}
                <p className="small-note">{t("confirm.autoVerifyHint")}</p>
                <div className="payway-diag" data-testid="payway-diagnostics">
                  <p className="diag-title">{t("confirm.diagTitle")}</p>
                  <div className="diag-row"><span>{t("confirm.diagEnv")}</span><strong data-testid="payway-diag-env">{payData.environment || "SANDBOX"}</strong></div>
                  <div className="diag-row"><span>{t("confirm.diagTranId")}</span><strong data-testid="payway-diag-tran-id">{payData.tran_id}</strong></div>
                  <div className="diag-row"><span>{t("confirm.diagStatus")}</span><strong data-testid="payway-diag-status-code">{payData.payway_status_code}</strong></div>
                  <div className="diag-row"><span>{t("confirm.diagMessage")}</span><strong data-testid="payway-diag-status-message">{payData.payway_status_message || "—"}</strong></div>
                  {payData.payway_trace_id && <div className="diag-row"><span>{t("confirm.diagTrace")}</span><strong data-testid="payway-diag-trace-id">{payData.payway_trace_id}</strong></div>}
                </div>
              </div>
            )}

            {method === "ABA QR" && payStatus === "pending" && (
              <div className="confirm-step qr-step" data-testid="qr-step">
                <span className="step-tag">STEP 2</span>
                <img src="/aba-qr.png" alt="ABA KHQR" className="qr-img" data-testid="aba-qr-image" />
                <p className="qr-note kh-first" data-testid="qr-scan-instruction">{t("confirm.qrScanInstruction")}</p>
                <p className="qr-note kh-first" data-testid="qr-note-kh">{QR_NOTE_KH}</p>
                <p className="qr-note" data-testid="qr-note-en">{QR_NOTE_EN}</p>
                <p className="remark-line">{t("confirm.remarkLabel")}: <strong>{confirmed.order_number}</strong></p>
                <p className="small-note">{t("confirm.qrBanksNote")}</p>
              </div>
            )}

            {method === "Other" && (
              <div className="confirm-step" data-testid="other-payment-note">
                <span className="step-tag">STEP 2</span>
                <p className="muted">{t("confirm.otherNote")}</p>
                {confirmed.telegram_phone && (
                  <p className="remark-line" data-testid="other-telegram-phone">
                    {t("owner.telegramPhone")}: <strong>{confirmed.telegram_phone}</strong>
                  </p>
                )}
              </div>
            )}

            {method !== "Other" && (
              <PaymentFollowUp order={confirmed} phone={form.phone} onUpdated={setConfirmed} />
            )}

            {user && (
              <p className="muted points-note" data-testid="points-earned">
                {t("confirm.earnedAfter")} +{Math.round(confirmed.total)} {t("auth.points")} ✿
              </p>
            )}

            <button
              className="checkout-btn btn-track"
              data-testid="confirm-track-button"
              onClick={() => { close(); navigate(`/track?id=${confirmed.order_number}`); }}
            >
              {t("confirm.track")}
            </button>
            <button className="btn btn-light continue-btn" data-testid="continue-shopping-button" onClick={close}>
              {t("confirm.continue")}
            </button>
          </div>
        ) : (
          <>
            <div className="modal-head">
              <div>
                <p className="section-title-small">{t("checkout.eyebrow")}</p>
                <h2>{t("checkout.title")}</h2>
              </div>
              <button className="close-btn" data-testid="checkout-close-button" aria-label="Close checkout" onClick={() => setCheckoutOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {!user && (
              <div className="guest-note" data-testid="guest-note">
                <span>{t("auth.guestNote")}</span>
                <button data-testid="checkout-login-button" onClick={() => openAuth("login")}>{t("auth.login")}</button>
              </div>
            )}

            <div className="checkout-grid">
              <form onSubmit={submit}>
                <div className="form-grid">
                  <div className="field">
                    <label>{t("checkout.name")}</label>
                    <input data-testid="checkout-name-input" name="name" value={form.name} onChange={set("name")} required />
                  </div>
                  <div className="field">
                    <label>{t("checkout.phone")}</label>
                    <input data-testid="checkout-phone-input" name="phone" value={form.phone} onChange={set("phone")} required />
                  </div>
                  <div className="field full">
                    <label>{t("auth.email")}</label>
                    <input data-testid="checkout-email-input" name="email" type="email" value={form.email} onChange={set("email")} />
                  </div>
                  <div className="field">
                    <label>{t("checkout.province")}</label>
                    <input data-testid="checkout-province-input" name="province" value={form.province} onChange={set("province")} required />
                  </div>
                  <div className="field">
                    <label>{t("checkout.district")}</label>
                    <input data-testid="checkout-district-input" name="district" value={form.district} onChange={set("district")} required />
                  </div>
                  <div className="field">
                    <label>{t("checkout.commune")}</label>
                    <input data-testid="checkout-commune-input" name="commune" value={form.commune} onChange={set("commune")} />
                  </div>
                  <div className="field">
                    <label>{t("checkout.village")}</label>
                    <input data-testid="checkout-village-input" name="address" value={form.address} onChange={set("address")} />
                  </div>
                  <div className="field full">
                    <label>{t("checkout.deliveryDetails")}</label>
                    <textarea data-testid="checkout-delivery-textarea" name="delivery" value={form.delivery} onChange={set("delivery")} />
                  </div>
                  <div className="field full">
                    <label>{t("checkout.remark")}</label>
                    <textarea data-testid="checkout-remark-textarea" name="remark" value={form.remark} onChange={set("remark")} />
                  </div>
                  {payment === "Other" && (
                    <div className="field full telegram-field" data-testid="telegram-phone-section">
                      <label>{t("checkout.telegramPhone")}</label>
                      <input
                        data-testid="checkout-telegram-phone-input"
                        name="telegram_phone"
                        value={form.telegram_phone}
                        onChange={set("telegram_phone")}
                        placeholder={t("checkout.telegramPhonePlaceholder")}
                      />
                      <small className="field-note">{t("checkout.telegramPhoneNote")}</small>
                    </div>
                  )}
                </div>

                <div className="payment-box">
                  <strong>{t("checkout.payment")}</strong>
                  <label className={`pay-option-card ${payment === "ABA PayWay" ? "on" : ""}`} data-testid="payment-option-payway">
                    <input
                      type="radio"
                      name="payment"
                      value="ABA PayWay"
                      checked={payment === "ABA PayWay"}
                      onChange={() => setPayment("ABA PayWay")}
                      data-testid="checkout-payment-payway-radio"
                    />
                    <span className="pay-titles">
                      <strong>{t("checkout.paywayTitle")}</strong>
                      <small>{t("checkout.paywayNote")}</small>
                    </span>
                  </label>
                  <label className={`pay-option-card ${payment === "ABA QR" ? "on" : ""}`} data-testid="payment-option-qr">
                    <input
                      type="radio"
                      name="payment"
                      value="ABA QR"
                      checked={payment === "ABA QR"}
                      onChange={() => setPayment("ABA QR")}
                      data-testid="checkout-payment-qr-radio"
                    />
                    <span className="pay-titles">
                      <strong>{t("checkout.bakongTitle")}</strong>
                      <small>{t("checkout.bakongNote")}</small>
                    </span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="Other"
                      checked={payment === "Other"}
                      onChange={() => setPayment("Other")}
                      data-testid="checkout-payment-other-radio"
                    />
                    {t("checkout.other")}
                  </label>
                  <p className="muted" style={{ fontSize: 12, marginTop: 14 }} data-testid="checkout-payment-note">
                    {payment === "Other" ? t("checkout.telegramPhoneNote") : t("checkout.note")}
                  </p>
                </div>

                <p className="error-text" data-testid="checkout-error">{error}</p>

                <button className="checkout-btn" type="submit" data-testid="checkout-submit-button" disabled={placing}>
                  {placing ? "…" : t("checkout.place")}
                </button>
              </form>

              <div className="checkout-summary" data-testid="checkout-summary">
                <h3>{t("checkout.summary")}</h3>
                <div data-testid="checkout-summary-items">
                  {cart.map((line) => (
                    <div className="summary-line" key={line.key}>
                      <div className="total-row summary-item">
                        <span>
                          {displayName(line)}
                          <small>
                            {line.group === "set"
                              ? `${t("opt.kneeShort")} ${line.options?.knee} · ${t("opt.bagShort")} ${line.options?.bag} × ${line.qty}`
                              : `${line.color} × ${line.qty}`}
                          </small>
                        </span>
                        <span>{money(lineTotal(line))}</span>
                      </div>
                      {(line.addons || []).map((a) => (
                        <div className="total-row addon-line" key={a.id}>
                          <span><small>+ {a.name} × {a.qty}</small></span>
                          <span><small>{money(a.price * a.qty)}</small></span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <form className="promo-row" onSubmit={applyPromo} data-testid="promo-section">
                  <label className="promo-label">{t("checkout.promoLabel")}</label>
                  <div className="promo-controls">
                    <input
                      data-testid="promo-code-input"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      placeholder={t("checkout.promoPlaceholder")}
                      aria-label={t("checkout.promoLabel")}
                      disabled={!!promoInfo}
                    />
                    {promoInfo ? (
                      <button type="button" className="promo-remove" data-testid="promo-remove-button" onClick={removePromo}>
                        {t("checkout.promoRemove")}
                      </button>
                    ) : (
                      <button className="promo-apply" type="submit" data-testid="promo-apply-button" disabled={promoBusy || !promoInput.trim()}>
                        {t("checkout.promoApply")}
                      </button>
                    )}
                  </div>
                  {promoMsg && <p className={promoMsg.ok ? "promo-ok" : "promo-err"} data-testid="promo-message">{promoMsg.text}</p>}
                </form>
                <div className="total-row">
                  <span>{t("cart.subtotal")}</span>
                  <span data-testid="checkout-subtotal">{money(promoInfo ? promoInfo.subtotal : cartTotal)}</span>
                </div>
                {promoInfo && (
                  <div className="total-row" data-testid="promo-line">
                    <span>{t("checkout.promoLine")} <strong>{promoInfo.code}</strong></span>
                    <span data-testid="checkout-discount">−{money(promoInfo.discount)}</span>
                  </div>
                )}
                <div className="total-row">
                  <span>{t("cart.delivery")}</span>
                  <span data-testid="checkout-delivery">{t("cart.free")}</span>
                </div>
                <div className="total-row main">
                  <span>{t("cart.total")}</span>
                  <span data-testid="checkout-total">{money(promoInfo ? promoInfo.final_total : cartTotal)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
